import { useEffect, useState, useCallback } from "react";

import { useAuth } from "../AuthContext";

import { useNavigate } from "react-router-dom";

import FacilityTile from "../components/FacilityTile";

import { getFacilitiesAPI } from "../services/api";

export default function AdminDashboard() {

    const navigate = useNavigate();

    const { user, logout } = useAuth();

    // =========================================
    // STATES
    // =========================================

    const [facilities, setFacilities] = useState([]);

    const [initialLoad, setInitialLoad] = useState(true);

    const [error, setError] = useState("");

    // =========================================
    // SELECTED DATE
    // =========================================

    const [selectedDate, setSelectedDate] = useState(
        new Date().toISOString().split("T")[0]
    );

    // =========================================
    // FETCH FACILITIES
    // showLoader = true
    // -> used only during first page load
    // =========================================

    const fetchFacilities = useCallback(

        async (showLoader = false) => {

            try {

                if (showLoader) {
                    setInitialLoad(true);
                }

                setError("");

                // ====================================
                // FORMAT DATE:
                // YYYY-MM-DD -> YYYYMMDD
                // ====================================

                const formattedDate =
                    selectedDate.replaceAll("-", "");

                const data =
                    await getFacilitiesAPI(
                        formattedDate
                    );

                setFacilities(data);

            } catch (err) {

                console.error(err);

                setError("Failed to load facilities");

            } finally {

                if (showLoader) {
                    setInitialLoad(false);
                }
            }
        },

        [selectedDate]
    );

    // =========================================
    // INITIAL LOAD + AUTO REFRESH
    // =========================================

    useEffect(() => {
        fetchFacilities(true); // initial load with spinner

        const interval = setInterval(() => {
            fetchFacilities(false); // silent background refresh
        }, 30000); // every 3 seconds

        return () => clearInterval(interval);

    }, [selectedDate, fetchFacilities]);


    // =========================================
    // OPEN FACILITY PAGE
    // =========================================

    const openFacility = (facility) => {

        navigate(`/facility/${facility.facility_id}`);
    };

    // =========================================
    // INITIAL LOADING SCREEN
    // ONLY SHOWN ON FIRST LOAD
    // =========================================

    if (initialLoad) {

        return (

            <div className="
                h-screen
                flex
                items-center
                justify-center
                bg-gray-100
            ">

                <div className="
                    text-xl
                    font-semibold
                    text-gray-600
                ">
                    Loading facilities...
                </div>

            </div>
        );
    }

    // =========================================
    // ERROR STATE
    // =========================================

    if (error) {

        return (

            <div className="
                h-screen
                flex
                items-center
                justify-center
                bg-gray-100
            ">

                <div className="
                    text-red-500
                    text-xl
                    font-semibold
                ">
                    {error}
                </div>

            </div>
        );
    }

    return (

        <div className="
    min-h-screen
    bg-gray-100
    p-4
    flex
    flex-col
">

            {/* ================================= */}
            {/* HEADER */}
            {/* ================================= */}

            <div className="
                flex
                items-center
                justify-between
                mb-4
                shrink-0
            ">

                <div>

                    <h1 className="
                        text-3xl
                        font-bold
                        text-gray-800
                    ">
                        Parking Dashboard
                    </h1>

                    <p className="
                        text-gray-500
                        mt-1
                        text-sm
                    ">
                        Welcome, {user?.username}
                    </p>

                </div>

                <button
                    onClick={logout}
                    className="
                        bg-red-500
                        hover:bg-red-600
                        text-white
                        px-5
                        py-2
                        rounded-xl
                        transition-all
                    "
                >
                    Logout
                </button>

            </div>

            {/* ================================= */}
            {/* DATE FILTER */}
            {/* ================================= */}

            <div className="
                mb-4
                flex
                items-center
                gap-3
                shrink-0
            ">

                <label className="
                    text-gray-700
                    font-medium
                    text-sm
                ">
                    Select Date:
                </label>

                <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) =>
                        setSelectedDate(e.target.value)
                    }
                    className="
                        border
                        border-gray-300
                        rounded-xl
                        px-3
                        py-2
                        bg-white
                        shadow-sm
                        text-sm
                        focus:outline-none
                        focus:ring-2
                        focus:ring-blue-400
                    "
                />

            </div>

            {/* ================================= */}
            {/* FACILITY GRID */}
            {/* ================================= */}

            <div className="flex-1">

                {facilities.length === 0 ? (

                    <div className="
                        h-full
                        bg-white
                        rounded-2xl
                        shadow-sm
                        flex
                        items-center
                        justify-center
                        text-gray-500
                    ">
                        No facilities available
                    </div>

                ) : (

                    <div className="
                        h-full

                        grid

                        grid-cols-2
                        md:grid-cols-3
                        lg:grid-cols-4
                        xl:grid-cols-5

                        gap-4

                        auto-rows-[160px]
                    ">

                        {facilities.map((facility) => (

                            <FacilityTile
                                key={facility.facility_key}
                                facility={facility}
                                onClick={openFacility}
                            />

                        ))}

                    </div>

                )}

            </div>

        </div>
    );
}
